import React, { Component, Fragment } from 'react';
import ReactDOM from 'react-dom';
import dateFormat from 'dateformat';
import BindingMessage from './binding-message';
import ButtonGroupMessage from './button-group-message';
import GameMessage from './game-message';
import RatingCard from './rating-card';
import NotificationCard from './notification-card';
import { t } from '../i18n';
import { replyFromLabel } from './reply-label.js';
import nativeBridge, { openLink } from '../widget/native-bridge.js';
import HScrollRow from '../common/h-scroll';
import { CARD } from '../widget/component-contract.js';
import LcIcon from '../widget/lc-icon.js';
import { isVoiceAttachmentUrl } from './voice-recorder.js';
import VoicePlayer from './voice-player.js';
import EmbedMessage, { EmbedFallbackCard, normalizeEmbedPayload } from './embed-message.js';

// 两条消息之间隔多久才插一条居中时间分隔线。
// 30min 而非 IM 常见的 5min：客服会话的节奏比熟人聊天慢得多——访客问一句、
// 等客服查单/转接、几分钟后再回，这仍是**同一段**对话。用 5min 会把一次完整
// 咨询切成一堆碎片，分隔线比消息还密，同时也把气泡分组切碎（头像/名字反复重复）。
// 注意：shouldShowTime（当前消息是否显示分隔线）和 nextShowsTime（下一条是否
// 另起一组，决定 isGroupEnd）两处判定必须共用这个常量，否则分组与分隔线会错位。
const TIME_DIVIDER_GAP_MS = 30 * 60 * 1000;


export default class MessageArea extends Component {
    constructor(props) {
        super(props);
        this.longPressTimer = null;
        this.longPressActive = null; // 当前正在长按的 messageId（用于视觉反馈）
        this.messageRefs = {};
        this.state = {
            previewImageUrl: null,
            submittedEmails: {},
            pressingId: null,        // 视觉缩放反馈
            pressProgress: 0,        // 0-100 进度，到 100 触发
            // 长按 / 右键弹出的动作菜单。此前长按是**直接**进入回复，访客想
            // 复制一段文字没有任何办法 —— 气泡上还挂着 userSelect:'none'
            //（为了让长按不被系统选词打断），连手动选中都做不到。
            actionMenu: null,        // { messageId, message, x, y, text }
            copyToast: null,         // 'ok' | 'fail' —— 复制后的轻提示
            hoverId: null,           // 桌面 hover 的消息 id（触屏永远为 null）
        };
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleContextMenu = this.handleContextMenu.bind(this);
        this.openImagePreview = this.openImagePreview.bind(this);
        this.closeImagePreview = this.closeImagePreview.bind(this);
    }

    openImagePreview(url) {
        this.setState({ previewImageUrl: url });
    }

    closeImagePreview() {
        this.setState({ previewImageUrl: null });
    }

    // 语音消息的时长提示——只是给 VoicePlayer 一个"先显示个数字，别一直 0:00"
    // 的 hint，不是权威值（真实时长由 <audio> 加载完 metadata 后自己纠正）。
    // TG/WhatsApp 等原生渠道的语音消息inbound 归一化时会把 duration 写进
    // metadata（见 ChannelMessageRouter::normalizeIncoming 的 voice 分支），
    // metadata 在这条链路上可能是 JSON 字符串也可能已经是对象，两种都要接住。
    _voiceDurationHint(message) {
        const raw = message && message.metadata;
        if (!raw) return 0;
        const meta = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch (e) { return null; } })() : raw;
        const d = meta && meta.duration;
        return (typeof d === 'number' && isFinite(d) && d > 0) ? d : 0;
    }


    componentDidUpdate(prevProps) {
        // 检查是否需要滚动到特定消息
        if (this.props.scrollToMessageId && this.props.scrollToMessageId !== prevProps.scrollToMessageId) {
            this.scrollToMessage(this.props.scrollToMessageId);
        }
    }
    
    scrollToMessage = (messageId) => {
        const messageRef = this.messageRefs[messageId];
        if (messageRef) {
            messageRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // 添加高亮效果
            messageRef.classList.add('highlight-message');
            setTimeout(() => {
                messageRef.classList.remove('highlight-message');
            }, 2000);
        }
    }

    // 消息列表委托点击：markdown 链接、link 卡片、KB 引用 chip 一网打尽。
    // 仅在 native 容器且 openUrl 能力就绪时接管；web 端保持 <a target=_blank> 原生行为。
    handleContentLinkClick = (e) => {
        // BindingMessage 等内层 <a onClick> 已经自行 openLink 并 preventDefault 时，
        // 事件仍会冒泡到这个委托监听——不挡住就是一次点击两个 call:openUrl。
        if (e.defaultPrevented) return;
        if (!(nativeBridge.isActive() && nativeBridge.supports('openUrl'))) return;
        const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (!a) return;
        const href = a.getAttribute('href');
        if (!href || href === '#') return;
        if (!/^(https?:\/\/|mailto:|tel:)/i.test(href)) return;
        e.preventDefault();
        const source = a.closest('.citation-chip') ? 'kb_citation' : 'message';
        openLink(href, { source });
    };

    // 长按视觉/触觉反馈：500ms 触发
    //   0ms     按下 → 气泡缩放 0.97 + 加 ring（pressing 态）
    //   ~480ms  即将触发 → 短震动（如有）+ 缩放 1.05 弹回
    //   500ms   触发 → showReplyOption
    //   松开    < 500ms：清状态、回 1.0；> 500ms：保持触发
    /**
     * 触屏 vs 鼠标。触屏靠长按出菜单，所以必须禁掉系统选词（否则一按就变成
     * 选中文字）；桌面有右键，没有这个冲突 —— 那就把文字选择还给用户，
     * 直接拖选复制才是桌面上最自然的做法。
     */
    _isCoarsePointer() {
        // 用户已经用手指碰过 → 一定是触屏，这是最硬的证据。
        if (this._sawTouch) return true;

        try {
            const mq = (q) => window.matchMedia && window.matchMedia(q).matches;
            // 有精确指针（鼠标 / 触控板，含二合一设备）→ 走桌面交互
            if (mq('(pointer: fine)')) return false;
            if (mq('(pointer: coarse)')) return true;
        } catch (_) { /* 老浏览器不支持 pointer 媒体查询 */ }

        // 媒体查询给不出结论（实测无头 Chrome 两个都返回 false，老浏览器同理）
        // → 退回能力探测
        return ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0;
    }

    componentWillUnmount() {
        // 组件卸载后 setState 会报 warning，且定时器会吊住已卸载的实例
        if (this._copyToastTimer) clearTimeout(this._copyToastTimer);
        if (this.longPressTimer) clearTimeout(this.longPressTimer);
        if (this._preHapticTimer) clearTimeout(this._preHapticTimer);
    }

    _startPress(messageId, message, evt) {
        // 还在生成中的临时气泡不挂菜单：文字每帧都在变，复制到的是半截；
        // 引用一条还没落库的消息更没有意义（它还没有真正的 id）。
        if (message && message._streaming) return;
        this.setState({ pressingId: messageId });
        // 触发点：优先用按下时的坐标，菜单就贴着手指/光标弹出
        const pt = this._pointFromEvent(evt);
        // 触发前的"将触"提示：480ms 时打个微震 + 缩放放大一点，告诉用户"再按一下就触发"
        this._preHapticTimer = setTimeout(() => {
            try { if (navigator.vibrate) navigator.vibrate(8); } catch (_) {}
        }, 480);
        this.longPressTimer = setTimeout(() => {
            try { if (navigator.vibrate) navigator.vibrate(20); } catch (_) {}
            this.openActionMenu(messageId, message, pt);
            this.setState({ pressingId: null });
        }, 500);
    }

    /**
     * 桌面 hover 态。触屏不跟踪 —— 移动浏览器的"粘性 hover"会在点一下之后
     * 一直保持 hover，图标就赖着不走了。
     */
    _setHover(id) {
        if (this._isCoarsePointer()) return;
        if (this.state.hoverId !== id) this.setState({ hoverId: id });
    }

    /**
     * 桌面 hover 出来的操作图标（复制 / 回复）。
     *
     * 为什么桌面要单独做一套：触屏靠长按出菜单（IM 通用语汇，不用学），
     * 但桌面没有长按 —— 我把它去掉了，因为"按住不动 500ms"正是用户准备拖选
     * 文字的动作，两者会打架。于是桌面只剩右键，而右键是没人会去试的隐藏入口。
     * hover 图标是可发现的，而且和客服工作台的交互一致。
     */
    _renderHoverActions(messageId, message, isRight) {
        if (this._isCoarsePointer()) return null;
        if (this.state.hoverId !== messageId) return null;
        if (message && message._streaming) return null;

        const text = this._copyableText(message);
        const btn = {
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', padding: 0, border: 0,
            // 颜色专项：底原为写死 rgba(255,255,255,.92)、字为 #6b7280 —— 暗色下
            // 是气泡边上一枚亮白药丸。这颗按钮只在 hover / 长按时出现，正是
            // "没人盯着的地方"，截图比对也照不到。
            borderRadius: 'var(--lc-radius-sm)', background: 'var(--lc-surface, #ffffff)',
            boxShadow: 'var(--lc-shadow-1)',
            color: 'var(--lc-text-2, #6b7280)', cursor: 'pointer',
        };
        // 零宽 flex item + 内部绝对定位 —— 图标不占任何布局宽度。
        //
        // 踩过两个坑：
        //   1. 按 <li> 绝对定位（right:-56px）：li 是整行满宽的 flex 容器，
        //      等于把图标推到整行之外，直接跑出视口。
        //   2. 当成正常 flex item：它会吃掉 56px 行宽，气泡的可用宽度跟着变，
        //      鼠标一悬停气泡就变窄、文字重新折行 —— 抖得很明显。
        // 所以容器 width:0（不参与宽度分配），图标在它内部绝对定位溢出显示。
        // order 决定落在气泡哪一侧：访客气泡靠右，order:-1 让图标落到它左边；
        // 客服气泡靠左，order:99 落到右边。
        return (
            <div
                style={{
                    position: 'relative',
                    width: 0, flex: '0 0 0', alignSelf: 'flex-start',
                    order: isRight ? -1 : 99,
                }}
            >
            <div
                style={{
                    position: 'absolute', top: '2px', whiteSpace: 'nowrap',
                    display: 'flex', gap: 'var(--lc-space-1)',
                    ...(isRight ? { right: '6px' } : { left: '6px' }),
                }}
            >
                {text ? (
                    <button type="button" style={btn} title={t('chat.action_copy')}
                            onClick={(e) => { e.stopPropagation(); this._copyFromHover(messageId, message); }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                    </button>
                ) : null}
                <button type="button" style={btn} title={t('chat.action_reply')}
                        onClick={(e) => { e.stopPropagation(); this.showReplyOption(messageId, message); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6M3 10l6-6" />
                    </svg>
                </button>
            </div>
            </div>
        );
    }

    /** hover 图标里的复制 —— 复用菜单那套 toast 反馈。 */
    _copyFromHover(messageId, message) {
        const text = this._copyableText(message);
        if (!text) return;
        this._copyText(text).then((ok) => {
            this.setState({ copyToast: ok ? 'ok' : 'fail' });
            if (this._copyToastTimer) clearTimeout(this._copyToastTimer);
            this._copyToastTimer = setTimeout(() => this.setState({ copyToast: null }), 1600);
        });
    }

    /** 从 touch / mouse 事件里取视口坐标 + 是否触屏；取不到就落在屏幕中间。 */
    _pointFromEvent(evt) {
        try {
            const t = evt && evt.touches && evt.touches[0];
            if (t) return { x: t.clientX, y: t.clientY, touch: true };
            if (evt && typeof evt.clientX === 'number') {
                return { x: evt.clientX, y: evt.clientY, touch: false };
            }
        } catch (_) {}
        return { x: window.innerWidth / 2, y: window.innerHeight / 2, touch: false };
    }
    _endPress() {
        if (this.longPressTimer) { clearTimeout(this.longPressTimer); this.longPressTimer = null; }
        if (this._preHapticTimer) { clearTimeout(this._preHapticTimer); this._preHapticTimer = null; }
        if (this.state.pressingId !== null) this.setState({ pressingId: null });
    }
    handleTouchStart(messageId, message, evt) {
        // touchstart 只可能由真实触摸触发，所以长按手势不需要任何能力判定。
        // 顺便记下这台设备是触屏，供 userSelect / hover 抑制使用。
        if (!this._sawTouch) {
            this._sawTouch = true;
            this.forceUpdate();   // userSelect 要从 text 切回 none
        }
        this._startPress(messageId, message, evt);
    }
    handleTouchEnd() { this._endPress(); }
    handleMouseDown() {
        // 鼠标一律不走长按："按住不动 500ms"正是用户准备拖选文字的动作，
        // 两者必然打架。鼠标用户有 hover 图标和右键，够用了。
        // 注意触屏浏览器也会在 touchend 后补发 mousedown —— 那时 _startPress
        // 已经由 touchstart 走过，这里直接不响应正好避免重复触发。
    }
    handleMouseUp() { this._endPress(); }

    handleContextMenu(e, messageId, message) {
        e.preventDefault();
        this._endPress();
        if (message && message._streaming) return;
        this.openActionMenu(messageId, message, this._pointFromEvent(e));
    }

    showReplyOption(messageId, message) {
        if (this.props.onReplyMessage) {
            this.props.onReplyMessage(messageId, message);
        }
    }

    // ---- 消息动作菜单（复制 / 回复）--------------------------------------

    /** 这条消息里可复制的纯文本；没有就返回空串，菜单据此不显示"复制"。 */
    _copyableText(message) {
        if (!message) return '';
        const raw = message.text != null ? message.text : message.content;
        return String(raw == null ? '' : raw).trim();
    }

    openActionMenu(messageId, message, pt) {
        const text = this._copyableText(message);
        const W = 150;
        const H = text ? 96 : 52;   // 只有"回复"时矮一半

        // 触屏上拇指从下方来，菜单弹在按下点正上方，别被手指盖住；
        // 上方没地方（消息在屏幕顶部）再往下放。鼠标右键沿用光标处。
        let y = pt.y;
        if (pt.touch) {
            y = (pt.y - H - 14 >= 8) ? (pt.y - H - 14) : (pt.y + 24);
        }

        // 贴边时往回收，别让它跑出视口
        const x = Math.max(8, Math.min(pt.x, window.innerWidth - W - 8));
        y = Math.max(8, Math.min(y, window.innerHeight - H - 8));
        // 长按弹出时手指还按在屏幕上，菜单和遮罩正好在指下。松手时浏览器先发
        // touchend，随后还会补一发合成的 click —— 直接打在遮罩上，菜单刚出现
        // 就被关掉。记下开启时刻，这个窗口内的"点外部关闭"一律忽略。
        this._menuOpenedAt = Date.now();
        this.setState({ actionMenu: { messageId, message, x, y, text } });
    }

    /**
     * 抬手会带出一串合成事件（touchend → mouse* → click），它们落在菜单刚
     * 弹出的位置上。菜单是贴着按下点弹的，所以第一个按钮正好在指下 ——
     * 不挡的话松手就直接触发"复制"，或者打在遮罩上把菜单关掉。
     *
     * 450ms 覆盖移动端 ~300ms 的合成延迟还留了余量；真人从看到菜单到点中
     * 一项也远不止这个时间，不会误伤真实操作。
     */
    _menuJustOpened() {
        return this._menuOpenedAt && Date.now() - this._menuOpenedAt < 450;
    }

    /** 点空白处关闭。菜单内的操作走各自的 handler。 */
    closeActionMenu = () => {
        if (this._menuJustOpened()) return;
        this.setState({ actionMenu: null });
    }

    /**
     * 复制。navigator.clipboard 在非安全上下文、或被 iframe 权限策略挡住时
     * （widget.html 被客户当 iframe 嵌入且没给 allow="clipboard-write"）会抛，
     * 所以保留 execCommand 兜底 —— 它虽然废弃了但不吃权限策略。
     */
    _copyText(text) {
        if (!text) return Promise.resolve(false);
        if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text)
                .then(() => true)
                .catch(() => this._copyFallback(text));
        }
        return Promise.resolve(this._copyFallback(text));
    }

    _copyFallback(text) {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.top = '-1000px';
            ta.style.opacity = '0';
            // closed shadow DOM 里 document.body 仍然可用（宿主页的 body）
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch (_) {
            return false;
        }
    }

    handleCopyMessage = () => {
        if (this._menuJustOpened()) return;   // 松手那一发合成 click，不是真点击
        const menu = this.state.actionMenu;
        if (!menu || !menu.text) return;
        const id = menu.messageId;
        this._copyText(menu.text).then((ok) => {
            this.setState({ actionMenu: null, copyToast: ok ? 'ok' : 'fail' });
            if (ok) { try { if (navigator.vibrate) navigator.vibrate(8); } catch (_) {} }
            if (this._copyToastTimer) clearTimeout(this._copyToastTimer);
            this._copyToastTimer = setTimeout(() => this.setState({ copyToast: null }), 1600);
        });
    }

    handleReplyFromMenu = () => {
        if (this._menuJustOpened()) return;   // 同上
        const menu = this.state.actionMenu;
        if (!menu) return;
        this.setState({ actionMenu: null }, () => {
            this.showReplyOption(menu.messageId, menu.message);
        });
    }

    /**
     * 复制后的轻提示。渲染在组件树内（= widget 的 shadow DOM 里），不用宿主页
     * 的任何 toast —— 宿主页未必有，而且那会把样式泄漏出去。
     * 贴着聊天区底部居中，1.6s 后自动消失，不挡输入框。
     */
    _renderCopyToast() {
        const st = this.state.copyToast;
        if (!st) return null;
        const ok = st === 'ok';
        return (
            <div
                aria-live="polite"
                style={{
                    position: 'fixed', left: '50%', bottom: '84px',
                    transform: 'translateX(-50%)', zIndex: 2147483002,
                    display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)',
                    padding: '8px 14px', borderRadius: 'var(--lc-radius-full)',
                    background: 'rgba(35,32,29,0.92)', color: '#fff',
                    fontSize: '13px', lineHeight: 1.2, whiteSpace: 'nowrap',
                    boxShadow: 'var(--lc-shadow-2)',
                    pointerEvents: 'none',
                    animation: 'lcCopyToastIn var(--lc-dur-fast) var(--lc-ease-out)',
                }}
            >
                {ok ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16h.01" />
                    </svg>
                )}
                {ok ? t('chat.copied') : t('chat.copy_failed')}
            </div>
        );
    }

    /**
     * 菜单本体。必须渲染在组件树内 —— widget 是 closed shadow DOM，
     * portal 到 document.body 会掉出 shadow root，样式和点击外部关闭都失效。
     */
    _renderActionMenu() {
        const menu = this.state.actionMenu;
        if (!menu) return null;
        const item = {
            display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', width: '100%',
            padding: '10px 14px', border: 0, background: 'transparent',
            font: 'inherit', fontSize: '14px', color: '#23201d',
            cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
        };
        return (
            <Fragment>
                {/* 点外部关闭。只挂 click —— onTouchStart 会在手指抬起前就触发，
                    等于绕过上面那道时间窗守卫，长按刚弹出就被自己关掉。 */}
                <div
                    onClick={this.closeActionMenu}
                    onContextMenu={(e) => { e.preventDefault(); this.closeActionMenu(); }}
                    style={{ position: 'fixed', inset: 0, zIndex: 2147483000 }}
                />
                <div
                    style={{
                        position: 'fixed', top: menu.y + 'px', left: menu.x + 'px',
                        zIndex: 2147483001, minWidth: '134px',
                        background: 'var(--lc-surface, #fff)', borderRadius: 'var(--lc-radius-md)',
                        boxShadow: 'var(--lc-shadow-3)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        overflow: 'hidden', padding: 'var(--lc-space-1) 0',
                    }}
                >
                    {menu.text ? (
                        <button type="button" style={item} onClick={this.handleCopyMessage}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            {t('chat.action_copy')}
                        </button>
                    ) : null}
                    <button type="button" style={item} onClick={this.handleReplyFromMenu}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6M3 10l6-6" />
                        </svg>
                        {t('chat.action_reply')}
                    </button>
                </div>
            </Fragment>
        );
    }

    /**
     * Render a non-text agent/admin message (button_group, game, …) inside
     * the same avatar + agent-name bubble shell that plain text messages get.
     *
     * Without this, those branches return a naked <li><div class="msg">{x}</div>,
     * which gives the visitor no signal of "who is talking" — makes the welcome
     * flow's button menu feel like an orphaned UI widget instead of a chat msg.
     *
     * Mirrors the avatar/name resolution in the regular text render path
     * (search for `from === 'agent' || from === 'admin'` ~line 1473).
     */
    _renderAgentSpecialBubble({ messageKey, messageId, message, from, conf, TimeLabel, content, enterCls }) {
        let showAvatar = false;
        let avatarUrl = null;
        let agentName = null;

        if (from === 'visitor') {
            if (message.visitor_avatar) { showAvatar = true; avatarUrl = message.visitor_avatar; }
        } else if (from === 'agent' || from === 'admin') {
            if (conf.show_agent_avatar !== false) {
                showAvatar = true;
                avatarUrl = message.agent_avatar || null;
            }
            if (message.agent_name) agentName = message.agent_name;
        }

        return (
            <Fragment key={`frag-${messageKey}`}>
                {TimeLabel}
                <li className={from + ' chat-msg' + (enterCls || '')}
                    key={messageKey}
                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                >
                    {showAvatar && (
                        <div className="msg-avatar" style={{
                            width: '36px', height: '36px', marginRight: '10px',
                            flexShrink: 0, alignSelf: 'flex-end',
                            borderRadius: 'var(--lc-radius-full)', overflow: 'hidden',
                            background: avatarUrl ? 'transparent' : ((conf && conf.mainColor) || '#2563eb'),
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '14px', fontWeight: 600,
                            boxShadow: 'var(--lc-shadow-1)',
                        }}>
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="avatar" style={{
                                    width: '100%', height: '100%', borderRadius: 'var(--lc-radius-full)',
                                    objectFit: 'cover', display: 'block',
                                }} onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 10-16 0"/></svg>');
                                    e.target.style.background = 'var(--lc-surface-3, #e2e8f0)';
                                    e.target.style.padding = '4px';
                                }} />
                            ) : (
                                <span>{(agentName || 'CS').charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                    )}
                    <div className="msg-wrapper" style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                        {agentName && (
                            <div className="agent-name" style={{
                                fontSize: '13px', color: 'var(--lc-text-2, #64748b)',
                                marginBottom: 'var(--lc-space-1)', fontWeight: '500'
                            }}>{agentName}</div>
                        )}
                        <div className="msg">{content}</div>
                    </div>
                </li>
            </Fragment>
        );
    }

    // 访客消息的发送态视觉反馈：sending 显示"发送中"，failed 显示可点重发的提示。
    // 之前 tempMessage(status:'sending') 永久停在界面上,和已送达消息毫无区别——
    // 网络抖动时用户完全不知道消息有没有发出去。failed 态点击调用 onResendMessage
    // (chat.js 负责移除旧气泡 + 用原文本/附件重新走一遍 sendMessage)。
    _renderSendStatus(message) {
        if (message.from !== 'visitor') return null;
        if (message.status !== 'sending' && message.status !== 'failed') return null;
        const failed = message.status === 'failed';
        return (
            <div
                className="msg-send-status"
                onClick={(e) => {
                    if (failed && this.props.onResendMessage) {
                        e.stopPropagation();
                        this.props.onResendMessage(message);
                    }
                }}
                style={{
                    fontSize: '11px',
                    marginTop: 'var(--lc-space-1)',
                    textAlign: 'right',
                    color: failed ? '#e53e3e' : 'var(--lc-text-3, #999)',
                    cursor: failed ? 'pointer' : 'default',
                    textDecoration: failed ? 'underline' : 'none',
                }}
            >
                {failed ? t('msg.send_failed') : t('msg.sending')}
            </div>
        );
    }

    // 处理邮箱提交（用于 ask_email 节点）
    // 注意：widget 挂在 closed Shadow DOM 里，document.getElementById 找不到
    // shadow 内元素 —— 必须走 ref（_emailInputRefs），getElementById 仅作
    // 独立 chat.html（无 shadow）的兜底。
    handleEmailSubmit(messageId, emailCardId) {
        const emailInput = (this._emailInputRefs && this._emailInputRefs[emailCardId])
            || document.getElementById(`${emailCardId}-input`);
        if (!emailInput) return;

        const email = emailInput.value.trim();
        if (!email) {
            alert(t('email.error_enter_address'));
            return;
        }

        // 简单邮箱格式验证
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert(t('email.error_invalid_address'));
            return;
        }

        // 调用父组件的邮箱提交方法
        if (this.props.onEmailSubmit) {
            this.props.onEmailSubmit(email).then(() => {
                // 更新状态标记为已提交
                this.setState(prev => ({
                    submittedEmails: {
                        ...prev.submittedEmails,
                        [messageId]: email
                    }
                }));
            }).catch(err => {
                console.error('Email submit error:', err);
                alert(t('email.error_send_failed'));
            });
        } else {
            // 如果没有回调，直接标记为已提交
            this.setState(prev => ({
                submittedEmails: {
                    ...prev.submittedEmails,
                    [messageId]: email
                }
            }));
        }
    }

    // ===== Rich message helpers (spec 2026-04-17-rich-messages §9) =====
    //
    // Covers: markdown, card, buttons, carousel, form, link.
    // Uses inline styles only (widget.js ships no extra CSS file).

    _richMeta(message) {
        let meta = message.metadata;
        if (typeof meta === 'string') {
            try { meta = JSON.parse(meta); } catch { meta = {}; }
        }
        return (meta && typeof meta === 'object') ? meta : {};
    }

    _richTypeOf(message) {
        const t = message.type || message.message_type;
        // Accept the canonical spec types; everything else falls through to text.
        // 'embed' = 嵌入式 WebView 消息(spec 2026-08-07 补充节),渲染器见 embed-message.js。
        if (['markdown','card','buttons','carousel','form','link','embed'].indexOf(t) !== -1) return t;
        return null;
    }

    _fallbackText(message) {
        const meta = this._richMeta(message);
        return message.fallback_text || meta.fallback_text || message.text || message.content || '';
    }

    _richPayload(message) {
        const meta = this._richMeta(message);
        return message.payload || meta.payload || {};
    }

    _richSafeUrl(url) {
        if (!url || typeof url !== 'string') return '#';
        if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
        return '#';
    }

    _escapeHtml(s) {
        return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
    }

    // tiny GFM parser → React elements (safe subset: no raw HTML)
    renderMarkdownToReact(md) {
        if (!md) return null;
        const escapeHtml = (s) => this._escapeHtml(s);
        const inline = (s) => {
            let out = escapeHtml(s);
            out = out.replace(/`([^`]+?)`/g, (_m,c) => `<code style="background:var(--lc-surface-2, #f3f4f6);padding:1px 4px;border-radius:var(--lc-radius-xs);font-size:0.9em">${c}</code>`);
            out = out.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
            out = out.replace(/__([^_]+?)__/g, '<strong>$1</strong>');
            out = out.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
            out = out.replace(/(^|[^_])_([^_\n]+?)_(?!_)/g, '$1<em>$2</em>');
            out = out.replace(/\[([^\]]+?)\]\(([^)\s]+)\)/g, (_m, text, url) => {
                const safe = /^(https?:\/\/|mailto:|tel:)/i.test(url) ? url : '#';
                return `<a href="${safe}" target="_blank" rel="noopener noreferrer" style="color:var(--lc-main);text-decoration:underline">${text}</a>`;
            });
            return out;
        };
        const lines = String(md).replace(/\r\n/g,'\n').split('\n');
        const blocks = [];
        let i = 0, inList = null, inBlockquote = false, paraBuf = [];
        const flushPara = () => { if (paraBuf.length) { blocks.push(`<p style="margin:0.4em 0">${inline(paraBuf.join(' '))}</p>`); paraBuf = []; } };
        const closeList = () => { if (inList) { blocks.push(`</${inList}>`); inList = null; } };
        const closeBlockquote = () => { if (inBlockquote) { blocks.push('</blockquote>'); inBlockquote = false; } };
        while (i < lines.length) {
            const line = lines[i];
            const trimmed = line.trim();
            if (/^```/.test(trimmed)) {
                flushPara(); closeList(); closeBlockquote(); i++;
                const buf = [];
                while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
                i++;
                blocks.push(`<pre style="background:#1f2937;color:#f3f4f6;padding:0.6em;border-radius:var(--lc-radius-sm);overflow-x:auto;margin:0.4em 0"><code>${escapeHtml(buf.join('\n'))}</code></pre>`);
                continue;
            }
            if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) { flushPara(); closeList(); closeBlockquote(); blocks.push('<hr style="border:0;border-top:var(--lc-hairline);margin:0.6em 0"/>'); i++; continue; }
            if (trimmed.includes('|') && i+1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i+1]) && lines[i+1].includes('-')) {
                flushPara(); closeList(); closeBlockquote();
                const split = (row) => row.replace(/^\s*\|/,'').replace(/\|\s*$/,'').split('|').map(c=>c.trim());
                const headers = split(trimmed); i += 2;
                let tbl = '<table style="border-collapse:collapse;margin:0.4em 0"><thead><tr>';
                tbl += headers.map(h => `<th style="border:var(--lc-hairline);padding:var(--lc-space-1) var(--lc-space-2);background:var(--lc-bg, #f9fafb)">${inline(h)}</th>`).join('');
                tbl += '</tr></thead><tbody>';
                while (i < lines.length && lines[i].trim().includes('|')) {
                    const cells = split(lines[i].trim());
                    tbl += '<tr>' + cells.map(c => `<td style="border:var(--lc-hairline);padding:4px 8px">${inline(c)}</td>`).join('') + '</tr>';
                    i++;
                }
                tbl += '</tbody></table>';
                blocks.push(tbl);
                continue;
            }
            const hm = /^(#{1,6})\s+(.*)$/.exec(trimmed);
            if (hm) { flushPara(); closeList(); closeBlockquote(); const lv = hm[1].length; blocks.push(`<h${lv} style="margin:0.4em 0;font-weight:600;font-size:${1.4 - (lv-1)*0.1}em">${inline(hm[2])}</h${lv}>`); i++; continue; }
            if (/^>\s?/.test(trimmed)) { flushPara(); closeList(); if (!inBlockquote) { blocks.push('<blockquote style="border-left:3px solid var(--lc-border, #d1d5db);padding-left:10px;color:var(--lc-text-2, #4b5563);margin:0.4em 0">'); inBlockquote = true; } blocks.push(`<p style="margin:0.2em 0">${inline(trimmed.replace(/^>\s?/,''))}</p>`); i++; continue; } else { closeBlockquote(); }
            const ulm = /^[-*+]\s+(.*)$/.exec(trimmed);
            if (ulm) { flushPara(); if (inList !== 'ul') { closeList(); blocks.push('<ul style="padding-left:1.4em;margin:0.4em 0;list-style:disc">'); inList = 'ul'; } blocks.push(`<li>${inline(ulm[1])}</li>`); i++; continue; }
            const olm = /^\d+\.\s+(.*)$/.exec(trimmed);
            if (olm) { flushPara(); if (inList !== 'ol') { closeList(); blocks.push('<ol style="padding-left:1.4em;margin:0.4em 0;list-style:decimal">'); inList = 'ol'; } blocks.push(`<li>${inline(olm[1])}</li>`); i++; continue; }
            if (inList && trimmed === '') { closeList(); i++; continue; }
            if (trimmed === '') { flushPara(); i++; continue; }
            paraBuf.push(trimmed); i++;
        }
        flushPara(); closeList(); closeBlockquote();
        return <div className="lc-markdown" dangerouslySetInnerHTML={{ __html: blocks.join('\n') }} />;
    }

    /**
     * Body text for an agent/AI bubble. AI replies routinely contain Markdown
     * (**bold**, lists, `code`, links, tables) — render those through the same
     * HTML-escaping Markdown renderer used by the rich 'markdown' type, so the
     * visitor sees formatting instead of raw asterisks. Human/visitor messages
     * stay plain text (renderer is gated to is_ai to avoid mangling stray * or _).
     */
    renderMessageBody(text, displayName, message) {
        const body = displayName ? `${displayName}: ${text}` : text;
        const isAi = !!(message && (message.is_ai || message.is_ai_response));
        if (isAi && text) {
            const md = this.renderMarkdownToReact(body);
            if (md) return md;
        }
        // 人工客服/系统消息：纯文本 + 裸 URL linkify（先整体 escape 再替换，绝不引入注入面）
        const escaped = this._escapeHtml(String(body || ''));
        const linkified = escaped.replace(
            /(https?:\/\/[^\s<>"']+)/gi,
            (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer">${m}</a>`
        );
        return <p dangerouslySetInnerHTML={{ __html: linkified }} />;
    }

    handleRichButtonClick(message, button) {
        const action = button && button.action;
        const silent = action && action.silent === true;
        if (!action) return;
        if (action.type === 'url') {
            // 后端 validator 允许 URL 放在 action.value，做别名兜底
            // action.target 不再使用——服务端 Task 5 起只允许 _blank
            const url = this._richSafeUrl(action.url || action.value);
            if (url !== '#') openLink(url, { source: 'rich_button' });
            return;
        }
        if (action.type === 'phone') {
            const n = (action.number || '').replace(/[^\d+]/g, '');
            if (n) window.location.href = 'tel:' + n;
            return;
        }
        // postback / next_step / submit_form → delegate to chat layer
        if (this.props.onButtonClick) {
            this.props.onButtonClick({
                id: button.id,
                text: button.text,
                value: action.value || action.step_id || '',
                action,
                silent: !!silent,
            }, message.id || message.messageId);
        }
    }

    renderRichButtonRow(message, buttons, layout) {
        if (!buttons || !buttons.length) return null;
        const layoutStyle = (() => {
            if (layout === '1col') return { display: 'flex', flexDirection: 'column', gap: 'var(--lc-space-2)' };
            if (layout === '2col') return { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--lc-space-2)' };
            if (layout === '3col') return { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--lc-space-2)' };
            return { display: 'flex', flexWrap: 'wrap', gap: 'var(--lc-space-2)' };
        })();
        return (
            <div className="lc-btn-row" style={{ marginTop: 'var(--lc-space-2)', ...layoutStyle }}>
                {buttons.map((btn, i) => {
                    // url/phone 只在访客本地打开，会话关了也能用；回传型按钮才受门控
                    const actionType = btn.action && btn.action.type;
                    const lockedBtn = !!this.props.interactionLocked && actionType !== 'url' && actionType !== 'phone';
                    return (
                    <button
                        key={btn.id || i}
                        className="lc-btn"
                        disabled={lockedBtn}
                        onClick={() => this.handleRichButtonClick(message, btn)}
                        style={{
                            padding: '7px 14px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--lc-main)',
                            background: 'var(--lc-main-soft)',
                            border: '1px solid var(--lc-main-outline)',
                            borderRadius: 'var(--lc-radius-full)',
                            cursor: lockedBtn ? 'not-allowed' : 'pointer',
                            opacity: lockedBtn ? 0.6 : 1,
                            textAlign: 'center',
                        }}
                    >
                        {btn.text}
                    </button>
                    );
                })}
            </div>
        );
    }

    renderRichCard(message, payload, { compact = false } = {}) {
        const ratio = payload.image_ratio || '16:9';
        const aspectRatio = ratio.replace(':', '/');
        return (
            <div className="lc-card" style={{
                background: 'var(--lc-surface, #ffffff)',
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-md)',
                overflow: 'hidden',
                maxWidth: compact ? '220px' : '320px',
                boxShadow: 'var(--lc-shadow-1)',
            }}>
                {payload.image_url && (
                    <div className="lc-card__img" style={{ background: 'var(--lc-surface-2, #f3f4f6)', aspectRatio }}>
                        <img src={payload.image_url} alt={payload.title || ''}
                             style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                             onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                )}
                <div className="lc-card__body" style={{ padding: 'var(--lc-space-3)' }}>
                    {payload.title && <div className="lc-card__title" style={{ fontSize: '15px', fontWeight: 600, color: 'var(--lc-text, #111827)' }}>{payload.title}</div>}
                    {payload.subtitle && <div className="lc-card__subtitle" style={{ fontSize: '13px', color: 'var(--lc-main)', marginTop: '2px' }}>{payload.subtitle}</div>}
                    {payload.description && <p className="lc-card__desc" style={{ fontSize: '13px', color: 'var(--lc-text-2, #4b5563)', margin: '6px 0 0 0', lineHeight: 1.45 }}>{payload.description}</p>}
                </div>
                {payload.buttons && payload.buttons.length > 0 && (
                    <div className="lc-card__actions" style={{ borderTop: 'var(--lc-hairline)', display: 'flex', flexDirection: 'column' }}>
                        {payload.buttons.map((btn, i) => (
                            <button key={btn.id || i}
                                    onClick={() => this.handleRichButtonClick(message, btn)}
                                    style={{
                                        padding: 'var(--lc-space-2) var(--lc-space-3)',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: 'var(--lc-main)',
                                        background: 'transparent',
                                        border: 'none',
                                        borderBottom: i < payload.buttons.length - 1 ? 'var(--lc-hairline)' : 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                    }}>
                                {btn.text}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    renderRichCarousel(message, payload) {
        const cards = payload.cards || [];
        const key = message.id || message.messageId;
        const page = (this.state.carouselPage || {})[key] || 0;
        // R10：翻页指示点。桌面靠两端渐隐 + hover 箭头就能看出"右边还有"，触屏
        // 两者都不显形 —— 卡片切齐在容器右边缘，看上去就是一张卡。几何走
        // CARD.dot*（三端同一张表），纯指示不可点（可点就得吃 44 命中区，一排
        // 6px 的点撑到 44 就不成一排了；翻页本身已有滑动/箭头两条路）。
        const dots = cards.length >= CARD.dotsMinCards ? (
            <div className="lc-carousel__dots" aria-hidden="true" style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${CARD.dotGapPx}px`,
                marginTop: `${CARD.dotsMarginTopPx}px`,
            }}>
                {cards.map((_, i) => (
                    <span key={i} style={{
                        width: `${CARD.dotPx}px`,
                        height: `${CARD.dotPx}px`,
                        borderRadius: 'var(--lc-radius-full)',
                        // 只换色不换尺寸：宽度动画会让整排点横向抖动。
                        background: i === page ? `var(${CARD.dotActiveColorToken})` : `var(${CARD.dotIdleColorToken})`,
                        transition: 'background var(--lc-dur-fast) var(--lc-ease-out)',
                        flexShrink: 0,
                    }} />
                ))}
            </div>
        ) : null;
        return (
            <div className="lc-carousel-wrap">
                {/* 和知识库分类条同一套横滚行为：两端渐隐 + 桌面拖拽 + 滚轮转横滚。
                    渐隐色跟消息区底色（富消息的气泡是透明的，背后就是 .chat 的 #f8fafc）。 */}
                <HScrollRow
                    className="lc-carousel"
                    // 颜色专项：这里原本把渐隐色写死成 #f8fafc / rgba(248,250,252,0)，
                    // 暗色下两端就是两道亮边。HScrollRow 的默认值本来就是
                    // var(--lc-bg, #f8fafc) → transparent —— 同一个颜色的 token 版，
                    // 删掉覆写即可。（注释里说"跟消息区底色"，而 --lc-bg 正是它。）
                    onPageChange={dots ? ((i) => this.setState((prev) => ({
                        carouselPage: { ...(prev.carouselPage || {}), [key]: i },
                    }))) : null}
                    style={{
                        display: 'flex',
                        gap: 'var(--lc-space-2)',
                        paddingBottom: '6px',
                        scrollSnapType: 'x mandatory',
                    }}
                >
                    {cards.map((c, i) => (
                        <div key={i} className="lc-carousel__item" style={{ flex: '0 0 auto', scrollSnapAlign: 'start' }}>
                            {this.renderRichCard(message, c, { compact: true })}
                        </div>
                    ))}
                </HScrollRow>
                {dots}
            </div>
        );
    }

    renderRichLink(message, payload) {
        const href = this._richSafeUrl(payload.url);
        return (
            <a className="lc-link-preview" href={href} target="_blank" rel="noopener noreferrer"
               style={{
                   display: 'block',
                   background: 'var(--lc-surface, #ffffff)',
                   border: 'var(--lc-hairline)',
                   borderRadius: 'var(--lc-radius-md)',
                   boxShadow: 'var(--lc-shadow-1)',
                   overflow: 'hidden',
                   maxWidth: '320px',
                   textDecoration: 'none',
                   color: 'inherit',
               }}>
                {payload.image_url && (
                    <div style={{ background: 'var(--lc-surface-2, #f3f4f6)', aspectRatio: '16/9' }}>
                        <img src={payload.image_url} alt={payload.title || ''}
                             style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                             onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                )}
                <div style={{ padding: 'var(--lc-space-3)' }}>
                    {(payload.site_name || payload.favicon_url) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)', fontSize: '12px', color: 'var(--lc-text-2, #6b7280)', marginBottom: 'var(--lc-space-1)' }}>
                            {payload.favicon_url && <img src={payload.favicon_url} width="14" height="14" alt="" onError={(e)=>{e.target.style.display='none';}} />}
                            <span>{payload.site_name || ''}</span>
                        </div>
                    )}
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--lc-text, #111827)' }}>{payload.title || payload.url}</div>
                    {payload.description && <p style={{ fontSize: '12px', color: 'var(--lc-text-2, #4b5563)', margin: 'var(--lc-space-1) 0 0 0', lineHeight: 1.45 }}>{payload.description}</p>}
                    {payload.url && <div style={{ fontSize: '11px', color: 'var(--lc-main)', marginTop: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{payload.url}</div>}
                </div>
            </a>
        );
    }

    renderRichForm(message, payload) {
        const formId = 'lc-form-' + (message.id || Math.random().toString(36).slice(2, 8));
        // 提交态有两个来源，服务端那份是权威的：
        //   payload.submitted —— 服务端拉历史时按 metadata.form_submission.message_id
        //                        回填（见 ApiController 历史分支），**跨会话有效**；
        //   本地 state       —— 刚点完提交那一瞬间的即时反馈，服务端回执还没回来。
        // 只有本地这一份的话，"重新进入对话"就是新的组件实例，提交态丢失、表单
        // 原样再摆一遍，用户会以为没提交成功。
        const submittedFlag = !!(payload && payload.submitted)
            || !!(this.state.submittedEmails && this.state.submittedEmails[formId]);
        const handleSubmit = (e) => {
            e.preventDefault();
            const data = {};
            const form = e.target;
            (payload.fields || []).forEach((f) => {
                const el = form.elements.namedItem(f.id);
                if (!el) return;
                if (f.type === 'checkbox') data[f.id] = !!el.checked;
                else data[f.id] = el.value;
            });
            // Submission goes through chat.js → ApiService.submitForm
            // (POST {apiUrl}/forms/submit per spec §9/§12.3) — a relative
            // fetch here would hit the host site's domain, not ours.
            if (this.props.onFormSubmit) {
                this.props.onFormSubmit({ message_id: message.id, form_id: payload.form_id || null, values: data });
            }
            // local UX: mark as submitted
            this.setState((prev) => ({ submittedEmails: { ...prev.submittedEmails, [formId]: true } }));
        };
        if (submittedFlag) {
            return (
                <div className="lc-form lc-form--submitted" style={{
                    padding: 'var(--lc-space-3)', background: 'var(--lc-surface-2, #f1f5f9)',
                    border: 'var(--lc-hairline)',
                    borderRadius: 'var(--lc-radius-md)', color: 'var(--lc-success)', fontSize: '13px',
                }}>{t('msg.submitted_thanks')}</div>
            );
        }
        // Server-side interpolated prefill: field.default (preferred) or
        // field.value. Missing/empty → undefined so React leaves the input
        // uncontrolled and empty.
        const prefillOf = (f) => {
            const v = f.default != null && f.default !== '' ? f.default : f.value;
            return v == null || v === '' ? undefined : String(v);
        };
        // R12:7 和 10 都不在 4 基数标尺上(历史手调值),收到 space-2 × space-3。
        const inputStyle = {
            width: '100%',
            padding: 'var(--lc-space-2) var(--lc-space-3)',
            fontSize: '13px',
            border: 'var(--lc-hairline)',
            borderRadius: 'var(--lc-radius-sm)',
            background: 'var(--lc-surface, #ffffff)',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
        };
        return (
            <form className="lc-form" onSubmit={handleSubmit} style={{
                background: 'var(--lc-surface, #ffffff)',
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-md)',
                boxShadow: 'var(--lc-shadow-1)',
                padding: 'var(--lc-space-3)',
                maxWidth: '320px',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--lc-space-2)',
            }}>
                {payload.title && <div className="lc-form__title" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--lc-text, #111827)' }}>{payload.title}</div>}
                {(payload.fields || []).map((f, i) => (
                    <label key={f.id || i} className="lc-field" style={{ display: 'block', fontSize: '12px', color: 'var(--lc-text-2, #374151)' }}>
                        <span className="lc-field__label" style={{ display: 'block', marginBottom: '3px' }}>
                            {f.label}{f.required ? <span style={{ color: 'var(--lc-danger)' }}> *</span> : null}
                        </span>
                        {f.type === 'textarea' ? (
                            <textarea name={f.id} required={!!f.required} placeholder={f.placeholder || ''} rows={2}
                                      maxLength={f.maxlength || undefined}
                                      defaultValue={prefillOf(f)}
                                      style={{ ...inputStyle, resize: 'vertical' }} />
                        ) : f.type === 'select' ? (
                            <select name={f.id} required={!!f.required} style={inputStyle} defaultValue={prefillOf(f) || ''}>
                                <option value="" disabled>{t('msg.select_placeholder')}</option>
                                {(f.options || []).map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        ) : f.type === 'checkbox' ? (
                            <input name={f.id} type="checkbox"
                                   defaultChecked={f.default === true || f.value === true || f.default === 'true' || f.value === 'true'}
                                   style={{ marginRight: '6px' }} />
                        ) : (
                            <input
                                name={f.id}
                                type={f.type === 'phone' ? 'tel' : f.type}
                                required={!!f.required}
                                placeholder={f.placeholder || ''}
                                min={f.min || undefined}
                                maxLength={f.maxlength || undefined}
                                defaultValue={prefillOf(f)}
                                style={inputStyle}
                            />
                        )}
                    </label>
                ))}
                <div className="lc-form__actions" style={{ display: 'flex', gap: 'var(--lc-space-2)', justifyContent: 'flex-end', marginTop: 'var(--lc-space-1)' }}>
                    <button type="submit" className="lc-btn lc-btn--primary" style={{
                        padding: '8px 14px', fontSize: '13px', fontWeight: 600,
                        color: 'var(--lc-bubble-user-text, #ffffff)', background: 'var(--lc-main)', border: 'none',
                        borderRadius: 'var(--lc-radius-sm)', cursor: 'pointer',
                    }}>{payload.submit_label || t('common.submit')}</button>
                </div>
            </form>
        );
    }

    renderRichMessage(message) {
        const type = this._richTypeOf(message);
        if (!type) return null;
        const payload = this._richPayload(message);
        try {
            if (type === 'markdown') return this.renderMarkdownToReact(payload.markdown || '');
            if (type === 'card')     return this.renderRichCard(message, payload);
            if (type === 'buttons')  return (
                <div>
                    {payload.text && <p style={{ margin: '0 0 6px 0' }}>{payload.text}</p>}
                    {this.renderRichButtonRow(message, payload.buttons || [], payload.layout || 'auto')}
                </div>
            );
            if (type === 'carousel') return this.renderRichCarousel(message, payload);
            if (type === 'form')     return this.renderRichForm(message, payload);
            if (type === 'link')     return this.renderRichLink(message, payload);
            if (type === 'embed') {
                // 协议非法(缺 height / url+html 互斥破坏 / 非 https)→ fallback_text 卡;
                // 连 fallback_text 都没有 → 返回 null,走默认文本气泡(text=content=fallback)。
                if (!normalizeEmbedPayload(payload)) {
                    const fb = this._fallbackText(message);
                    if (!fb) return null;
                    return <EmbedFallbackCard title={typeof payload.title === 'string' ? payload.title : ''} text={fb} />;
                }
                // embed_state:服务端权威的完成态(如已投票+结果),历史富化时写在
                // message.metadata.embed_state。传给 EmbedMessage → 经 bridge init 的
                // state 字段下发给 iframe,组件据此渲染"已完成"只读态(对齐评分卡
                // alreadyRated 的历史富化语义)。
                const embedMeta = this._richMeta(message);
                return <EmbedMessage
                    key={`embed-${message.id || ''}`}
                    payload={payload}
                    fallbackText={this._fallbackText(message)}
                    loadingLabel={t('common.loading')}
                    retryLabel={t('common.retry')}
                    completedLabel={t('embed.completed')}
                    contentLabel={t('embed.content_label')}
                    ackLabel={t('msg.submitted_thanks')}
                    failedLabel={t('embed.load_failed')}
                    bridge={this.props.embedBridge}
                    embedMessageId={message.id != null ? message.id : null}
                    theme={this.props.embedTheme}
                    mainColor={this.props.embedMainColor}
                    lang={this.props.embedLang}
                    embedState={embedMeta && embedMeta.embed_state ? embedMeta.embed_state : null}
                />;
            }
        } catch (e) {
            // swallow render errors → fallback text
            return <p className="lc-text lc-text--degraded">{this._fallbackText(message)}</p>;
        }
        return null;
    }

    renderCitationChips(message) {
        const meta = this._richMeta(message);
        const citations = (meta && meta.citations) || (message.metadata && message.metadata.citations);
        if (!citations || !citations.length) return null;
        return (
            <div className="citation-chips" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--lc-space-2)',
                marginTop: '6px',
            }}>
                {citations.map((c, i) => {
                    const title = c.source_title || c.title || t('msg.references');
                    const href = c.source_url ? this._richSafeUrl(c.source_url) : null;
                    const page = (c.page != null) ? ` · p.${c.page}` : '';
                    const content = (
                        <>
                            <svg className="citation-chip__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>[{i+1}] {title}{page}</span>
                        </>
                    );
                    const baseStyle = {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--lc-space-1)',
                        padding: '2px 8px',
                        fontSize: '11px',
                        color: '#1e40af',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: 'var(--lc-radius-full)',
                        maxWidth: '220px',
                        textDecoration: 'none',
                        cursor: href ? 'pointer' : 'default',
                    };
                    return href ? (
                        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="citation-chip" style={baseStyle}>{content}</a>
                    ) : (
                        <span key={i} className="citation-chip" style={baseStyle}>{content}</span>
                    );
                })}
            </div>
        );
    }

    render() {
        const { messages, conf, loadingHistory, hasMoreMessages } = this.props;
        const { previewImageUrl } = this.state;
        const currentTime = new Date();

        // 入场动画:只有"相对上一帧、纯追加到末尾"的新消息才播 message-enter。
        // 首帧(历史加载)与 prepend(向上加载更多)都不播，避免整屏历史一起滑入。
        const curIds = messages.map((m, i) => (m && m.id != null ? String(m.id) : `idx-${i}`));
        let animateFromIndex = Infinity;
        const prevIds = this._prevMsgIds;
        if (Array.isArray(prevIds) && curIds.length > prevIds.length) {
            let isPrefix = true;
            for (let i = 0; i < prevIds.length; i++) {
                if (prevIds[i] !== curIds[i]) { isPrefix = false; break; }
            }
            if (isPrefix) animateFromIndex = prevIds.length; // 只有尾部新增才是"追加"
        }
        this._prevMsgIds = curIds;

        return (
            <>
            <ol className="chat" role="log" aria-live="polite" onClick={this.handleContentLinkClick}>
                {/* 加载历史消息的指示器 —— 仅在已有消息时显示，避免空/新会话出现"向上滚动加载更多"指向虚无 */}
                {messages.length > 0 && (hasMoreMessages || loadingHistory) && (
                    <li className="system chat-msg" key="loading-history">
                        <div className="msg" style={{
                            background: 'var(--lc-surface-2, #f5f5f5)',
                            padding: 'var(--lc-space-2) var(--lc-space-3)',
                            borderRadius: 'var(--lc-radius-sm)',
                            fontSize: '13px',
                            textAlign: 'center',
                            marginBottom: '10px'
                        }}>
                            {loadingHistory ? (
                                <span>
                                    {t('chat.loading_history')}
                                    <span className="loading-dots" style={{marginLeft: '5px'}}>
                                        <span>.</span><span>.</span><span>.</span>
                                    </span>
                                </span>
                            ) : (
                                <span style={{color: 'var(--lc-text-2, #666)'}}>
                                    {t('chat.load_more_history')}
                                </span>
                            )}
                        </div>
                    </li>
                )}
                {messages.map((message, index) => {
                    const {name, text, from, attachment, time, type, binding_data, reply_to, id, service_data, message_type} = message;
                    const messageId = id || `msg-${index}`; // 使用消息ID或索引作为标识
                    const messageKey = id ? `msg-${id}` : `msg-${index}-${time}`; // 使用ID或索引+时间作为key
                    // 新追加的非系统消息 → 入场动画类(系统消息/时间分隔不参与)
                    const enterCls = (index >= animateFromIndex && from !== 'system') ? ' message-enter' : '';

                    // 防御性兜底：纯文本消息且 text/content/attachment/富媒体 payload 全空 → 不渲染空气泡。
                    // (服务端写入侧已有 guard，这里防历史脏数据和其他渠道漏网的空消息。)
                    {
                        const plainType = !type || type === 'text';
                        const plainMsgType = !message_type || message_type === 'text';
                        if (plainType && plainMsgType && !attachment && !binding_data && !service_data) {
                            const bodyText = (text != null ? String(text) : (message.content != null ? String(message.content) : '')).trim();
                            let hasRichPayload = false;
                            try {
                                const md = message.metadata
                                    ? (typeof message.metadata === 'string' ? JSON.parse(message.metadata) : message.metadata)
                                    : null;
                                hasRichPayload = !!(md && (md.payload || md.buttons || md.fallback_text));
                            } catch (e) { /* malformed metadata → treat as no payload */ }
                            if (bodyText === '' && !hasRichPayload) {
                                return null;
                            }
                        }
                    }

                    // 判断是否需要显示时间（类似微信逻辑）
                    let shouldShowTime = false;
                    let timeDisplay = '';
                    if (conf.displayMessageTime && time) {
                        if (index === 0) {
                            // 第一条消息总是显示时间
                            shouldShowTime = true;
                        } else {
                            const prevMessage = messages[index - 1];
                            if (prevMessage && prevMessage.time) {
                                const currentMsgTime = new Date(time);
                                const prevMsgTime = new Date(prevMessage.time);
                                const timeDiff = Math.abs(currentMsgTime - prevMsgTime);

                                // 间隔超过 TIME_DIVIDER_GAP_MS 才算"另起一段对话"
                                if (timeDiff > TIME_DIVIDER_GAP_MS) {
                                    shouldShowTime = true;
                                }

                                // 或者跨天了也显示
                                if (currentMsgTime.getDate() !== prevMsgTime.getDate() ||
                                    currentMsgTime.getMonth() !== prevMsgTime.getMonth() ||
                                    currentMsgTime.getFullYear() !== prevMsgTime.getFullYear()) {
                                    shouldShowTime = true;
                                }
                            }
                        }

                        if (shouldShowTime) {
                            // 带不带日期，看的是**同一自然日**，不是"距今 24 小时内"。
                            // 旧写法 `currentTime - msgTime < dayInMillis` 在凌晨会把
                            // 昨天的消息判成"今天"：00:30 时，昨天 23:50 的消息距今才
                            // 40 分钟，于是渲成裸 "23:50"，紧接着 00:05 又是裸 "00:05" ——
                            // 跨天信息彻底丢失。而分隔线现在是唯一的时间承载物，
                            // "这是昨天的事" 恰恰是它最该表达的东西之一。
                            const msgTime = new Date(time);
                            const isSameDay = msgTime.getDate() === currentTime.getDate()
                                && msgTime.getMonth() === currentTime.getMonth()
                                && msgTime.getFullYear() === currentTime.getFullYear();
                            if (isSameDay) {
                                timeDisplay = dateFormat(time, "HH:MM");
                            } else {
                                timeDisplay = dateFormat(time, "m/d HH:MM");
                            }
                        }
                    }

                    // 时间标签组件（微信风格：左右分隔线，中间时间）
                    const TimeLabel = shouldShowTime ? (
                        // lc-time-divider：唯一的时间承载物（气泡下方那份已删）。类名是
                        // 稳定契约 —— 无头测试靠它把分隔线和真·系统消息 li.system 区分开。
                        <li className="system chat-msg lc-time-divider" key={`time-${messageKey}`} style={{
                            padding: '15px 0',
                            textAlign: 'center',
                            listStyle: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span style={{
                                flex: '1',
                                height: '1px',
                                // 主题层 --lc-border（暗色 #2e3340），不是写死的
                                // rgba(0,0,0,.1) —— 后者在暗底上等于隐形，而分隔线
                                // 现在是**唯一**的时间承载物，隐形就等于时间丢了。
                                background: 'var(--lc-border, #e5e7eb)',
                                maxWidth: '100px'
                            }}></span>
                            <span style={{
                                // --lc-text-2 而非 --lc-text-3：后者在亮色下是
                                // #94a3b8 on #f8fafc = 2.45:1，12px 正文远低于
                                // WCAG AA。气泡下方那份时间删掉后，这行**是唯一**
                                // 的时间来源，看不清就等于没有时间。
                                // （--lc-text-2：亮 4.7:1 / 暗 7.4:1，运行时探针
                                //   锁在 message-timestamps.test.mjs ⑦-c）
                                color: 'var(--lc-text-2, #64748b)',
                                padding: '0 15px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap'
                            }}>
                                {timeDisplay}
                            </span>
                            <span style={{
                                flex: '1',
                                height: '1px',
                                // 主题层 --lc-border（暗色 #2e3340），不是写死的
                                // rgba(0,0,0,.1) —— 后者在暗底上等于隐形，而分隔线
                                // 现在是**唯一**的时间承载物，隐形就等于时间丢了。
                                background: 'var(--lc-border, #e5e7eb)',
                                maxWidth: '100px'
                            }}></span>
                        </li>
                    ) : null;
                    
                    // 处理临时状态消息
                    if (type === 'temp-status' || type === 'connection-status') {
                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className="system chat-msg" key={messageKey}>
                                <div className="msg" style={{
                                    background: type === 'connection-status' ? '#fff3cd' : 'var(--lc-surface-2, #f0f0f0)',
                                    color: type === 'connection-status' ? '#856404' : 'var(--lc-text-2, #666)',
                                    padding: 'var(--lc-space-2) var(--lc-space-3)',
                                    borderRadius: 'var(--lc-radius-sm)',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                    border: type === 'connection-status' ? '1px solid #ffeaa7' : 'none'
                                }}>
                                    <span>{text}</span>
                                    <span className="loading-dots" style={{marginLeft: '5px'}}>
                                        <span>.</span><span>.</span><span>.</span>
                                    </span>
                                </div>
                            </li>
                        </Fragment>);
                    }

                    // 处理绑定消息
                    if (type === 'binding' && binding_data) {
                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className={from + ' chat-msg' + enterCls}
                                key={messageKey}
                                ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                            >
                                <div className="msg">
                                    <BindingMessage bindingData={binding_data} theme={conf} />
                                </div>
                            </li>
                            </Fragment>
                        );
                    }

                    // 处理按钮组消息 —— 走和普通 admin/agent 文本一致的 avatar + name 包装
                    // (之前的实现直接 return 一个裸 <li><div class="msg"><ButtonGroupMessage/></div></li>，
                    //  跳过了头像槽位，导致 welcome flow 的按钮气泡看起来"没有客服身份"。)
                    if (message_type === 'button_group' || type === 'button_group') {
                        const metadata = this._richMeta(message);
                        const buttons = Array.isArray(metadata.buttons) ? metadata.buttons : [];
                        const layout = metadata.layout || 'auto';

                        return this._renderAgentSpecialBubble({
                            messageKey, messageId, message, from, conf, TimeLabel, enterCls,
                            content: (
                                <ButtonGroupMessage
                                    message={text}
                                    buttons={buttons}
                                    layout={layout}
                                    theme={conf}
                                    selectedId={metadata.selected_button_id}
                                    locked={!!this.props.interactionLocked}
                                    onButtonClick={(button) => {
                                        if (this.props.onButtonClick) {
                                            this.props.onButtonClick(button, messageId);
                                        }
                                    }}
                                />
                            ),
                        });
                    }

                    // 处理游戏消息 —— 同 button_group，走 avatar 包装
                    if (message_type === 'game' || type === 'game') {
                        const metadata = this._richMeta(message);
                        const gameUrl = metadata.game_url || '';
                        const gameType = metadata.game_type || '';

                        return this._renderAgentSpecialBubble({
                            messageKey, messageId, message, from, conf, TimeLabel, enterCls,
                            content: (
                                <GameMessage
                                    message={text}
                                    gameUrl={gameUrl}
                                    gameType={gameType}
                                    theme={conf}
                                    onGameClick={(url) => this.props.onServiceAction && this.props.onServiceAction('open_game', url)}
                                />
                            ),
                        });
                    }

                    // 处理系统通知消息（通话记录等）—— 居中气泡，无时间戳
                    if (from === 'system' && (message_type === 'call_record' || type === 'call_record')) {
                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className="system chat-msg" key={messageKey}
                                ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                style={{ listStyle: 'none' }}
                            >
                                <div className="msg" style={{
                                    background: 'var(--lc-surface-2, #f0f0f0)',
                                    color: 'var(--lc-text-2, #666)',
                                    padding: '6px 14px',
                                    borderRadius: 'var(--lc-radius-sm)',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    display: 'inline-block'
                                }}>
                                    <span>{text}</span>
                                </div>
                            </li>
                            </Fragment>
                        );
                    }

                    // ===== Rich messages (spec 2026-04-17-rich-messages §9) =====
                    // markdown / card / buttons / carousel / form / link
                    const richType = this._richTypeOf(message);
                    if (richType) {
                        const richNode = this.renderRichMessage(message);
                        if (richNode) {
                            const isVisitor = from === 'visitor';
                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className={from + ' chat-msg' + enterCls}
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg" style={{ background: 'transparent', padding: 0, boxShadow: 'none' }}>
                                        {richNode}
                                        {this.renderCitationChips(message)}
                                    </div>
                                </li>
                                </Fragment>
                            );
                        }
                    }

                    // 处理评分卡片消息
                    if (type === 'rating' || (type === 'service' && message.service_data?.type === 'rate_chat')) {
                        const ratingMeta = this._richMeta(message);
                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className="system chat-msg" key={messageKey}
                                ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                style={{ listStyle: 'none' }}
                            >
                                <div className="msg">
                                    <RatingCard
                                        chatId={message.chat_id || this.props.chatId}
                                        theme={conf}
                                        prompt={ratingMeta.prompt || message.service_data?.prompt || text}
                                        alreadyRated={!!(message.service_data?.rated || ratingMeta.rated)}
                                        ratingValue={message.service_data?.rating_value || ratingMeta.rating_value || 0}
                                    />
                                </div>
                            </li>
                            </Fragment>
                        );
                    }

                    // 处理服务消息
                    if (type === 'service' && message.service_data) {
                        const { service_data } = message;
                        
                        // 处理绑定请求消息（支持 offline_binding 和 request_binding）
                        if (service_data.type === 'offline_binding' || service_data.type === 'request_binding') {
                            const bindingMethods = service_data.binding_methods || [];
                            const allowMultiple = service_data.allow_multiple !== false;
                            const isOffline = service_data.type === 'offline_binding';

                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className="system chat-msg"
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg">
                                        <NotificationCard
                                            methods={bindingMethods}
                                            allowMultiple={allowMultiple}
                                            isOffline={isOffline}
                                            text={text}
                                            theme={conf}
                                            chat={this.props.chat}
                                            clientEmail={this.props.clientEmail}
                                            bindingStatus={this.props.bindingStatus}
                                        />
                                    </div>
                                </li>
                            </Fragment>
                        );
                        }

                        // 处理询问邮箱消息（显示邮箱收集卡片）
                        if (service_data.type === 'ask_email') {
                            const emailCardId = `email-card-${messageId}`;
                            // service_data.submitted：历史接口在访客已留邮箱时打的标记（刷新后保持已提交态）
                            const emailSubmitted = (this.state.submittedEmails && this.state.submittedEmails[messageId]) || service_data.submitted;

                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className="system chat-msg"
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg">
                                        <div style={{
                                            background: 'var(--lc-surface, #ffffff)',
                                            padding: 'var(--lc-space-4)',
                                            borderRadius: 'var(--lc-radius-md)',
                                            boxShadow: 'var(--lc-shadow-2)',
                                            border: 'var(--lc-hairline)',
                                            maxWidth: '320px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 'var(--lc-space-3)'
                                            }}>
                                                <span style={{
                                                    color: conf?.mainColor || '#2563eb',
                                                    marginRight: 'var(--lc-space-2)',
                                                    display: 'flex',
                                                    flexShrink: 0,
                                                }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                                                         strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                                                        <rect x="3" y="5" width="18" height="14" rx="2" />
                                                        <path d="M3.5 6.5 12 12.5l8.5-6" />
                                                    </svg>
                                                </span>
                                                <h4 style={{
                                                    margin: '0',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: 'var(--lc-text, #333)'
                                                }}>{t('msg.leave_email')}</h4>
                                            </div>
                                            <p style={{
                                                margin: '0 0 var(--lc-space-3) 0',
                                                fontSize: '14px',
                                                lineHeight: '1.5',
                                                color: 'var(--lc-text-2, #666)'
                                            }}>{text}</p>

                                            {emailSubmitted ? (
                                                <div style={{
                                                    padding: 'var(--lc-space-3)',
                                                    background: '#e8f5e9',
                                                    borderRadius: 'var(--lc-radius-sm)',
                                                    color: '#2e7d32',
                                                    fontSize: '14px',
                                                    textAlign: 'center'
                                                }}>
                                                    {t('msg.email_submitted')}
                                                </div>
                                            ) : (
                                                <div>
                                                    <input
                                                        type="email"
                                                        id={`${emailCardId}-input`}
                                                        ref={(el) => {
                                                            if (!this._emailInputRefs) this._emailInputRefs = {};
                                                            this._emailInputRefs[emailCardId] = el;
                                                        }}
                                                        placeholder={t('msg.email_placeholder')}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            border: 'var(--lc-hairline)',
                                                            borderRadius: 'var(--lc-radius-sm)',
                                                            fontSize: '14px',
                                                            marginBottom: '10px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                this.handleEmailSubmit(messageId, emailCardId);
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => this.handleEmailSubmit(messageId, emailCardId)}
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: '10px 14px',
                                                            background: 'var(--lc-main)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: 'var(--lc-radius-sm)',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            transition: 'transform var(--lc-dur-base), box-shadow var(--lc-dur-base)',
                                                            boxShadow: 'var(--lc-shadow-1)'
                                                        }}
                                                        onMouseOver={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = 'var(--lc-shadow-2)'; }}
                                                        onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'var(--lc-shadow-1)'; }}
                                                    >
                                                        <span>{t('common.submit')}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            </Fragment>
                        );
                        }

                        // 处理等待引导消息
                        if (service_data.type === 'wait_guidance') {
                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className="system chat-msg" 
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg">
                                        <div style={{
                                            background: 'var(--lc-surface, #ffffff)',
                                            padding: 'var(--lc-space-4)',
                                            borderRadius: 'var(--lc-radius-md)',
                                            boxShadow: 'var(--lc-shadow-2)',
                                            border: 'var(--lc-hairline)',
                                            maxWidth: '320px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 'var(--lc-space-3)'
                                            }}>
                                                <span style={{
                                                    color: conf?.mainColor || '#2563eb',
                                                    marginRight: 'var(--lc-space-2)',
                                                    display: 'flex',
                                                    flexShrink: 0,
                                                }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                                                         strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                                                        <circle cx="12" cy="8" r="3.5" />
                                                        <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
                                                    </svg>
                                                </span>
                                                <h4 style={{
                                                    margin: '0',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: 'var(--lc-text, #333)'
                                                }}>{t('msg.wait_guide')}</h4>
                                            </div>
                                            <p style={{ 
                                                margin: '0 0 var(--lc-space-4) 0',
                                                fontSize: '14px',
                                                lineHeight: '1.5',
                                                color: 'var(--lc-text-2, #666)'
                                            }}>{text}</p>
                                            <div style={{ 
                                                display: 'flex', 
                                                gap: 'var(--lc-space-2)', 
                                                flexWrap: 'wrap',
                                                marginTop: 'var(--lc-space-3)',
                                                paddingTop: 'var(--lc-space-3)',
                                                borderTop: 'var(--lc-hairline)'
                                            }}>
                                                {service_data.show_binding && (
                                                    <button 
                                                        onClick={() => this.props.onServiceAction && this.props.onServiceAction('show_push_binding')}
                                                        style={{
                                                            padding: '7px 12px',
                                                            background: 'var(--lc-surface, #ffffff)',
                                                            color: conf?.mainColor || '#2563eb',
                                                            border: 'var(--lc-hairline)',
                                                            borderRadius: 'var(--lc-radius-sm)',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            fontFamily: 'inherit',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('msg.bind_push')}
                                                    </button>
                                                )}
                                                {service_data.show_game && (
                                                    <button 
                                                        onClick={() => this.props.onServiceAction && this.props.onServiceAction('open_game', service_data.game_url)}
                                                        style={{
                                                            padding: '7px 12px',
                                                            background: 'var(--lc-surface, #ffffff)',
                                                            color: conf?.mainColor || '#2563eb',
                                                            border: 'var(--lc-hairline)',
                                                            borderRadius: 'var(--lc-radius-sm)',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            fontFamily: 'inherit',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {service_data.game_title}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                                </Fragment>
                            );
                        }
                    }

                    // 处理卡片消息
                    if (type === 'card') {
                        if (message.cardType === 'email-binding') {
                            const EmailBinding = message.emailBinding;
                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className={from + ' chat-msg' + enterCls} 
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg">
                                        {EmailBinding && EmailBinding.renderEmailBindingCard(message)}
                                    </div>
                                </li>
                            </Fragment>
                        );
                        }

                        if (message.cardType === 'service-invitation') {
                            return (
                                <Fragment key={`frag-${messageKey}`}>
                                    {TimeLabel}
                                <li className="system chat-msg" 
                                    key={messageKey}
                                    ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                >
                                    <div className="msg">
                                        <div style={{
                                            background: 'var(--lc-surface, #ffffff)',
                                            padding: 'var(--lc-space-4)',
                                            borderRadius: 'var(--lc-radius-md)',
                                            boxShadow: 'var(--lc-shadow-2)',
                                            border: 'var(--lc-hairline)',
                                            maxWidth: '320px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 'var(--lc-space-2)'
                                            }}>
                                                <LcIcon name="info" size={24} style={{
                                                    marginRight: '10px',
                                                    color: 'var(--lc-main)',
                                                }} />
                                                <h4 style={{
                                                    margin: '0',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: 'var(--lc-text, #333)'
                                                }}>{t('msg.system_tip')}</h4>
                                            </div>
                                            <p style={{ 
                                                margin: '0',
                                                fontSize: '14px',
                                                lineHeight: '1.5',
                                                color: 'var(--lc-text-2, #666)'
                                            }}>{text}</p>
                                        </div>
                                    </div>
                                </li>
                                </Fragment>
                            );
                        }
                    }

                    // 处理回复消息
                    if (type === 'reply' && reply_to) {
                        let displayName = name;
                        let showAvatar = false;
                        let avatarUrl = null;
                        let agentName = null;

                        if (from === 'visitor') {
                            displayName = null; // 访客消息不显示前缀
                            if (message.visitor_avatar) {
                                showAvatar = true;
                                avatarUrl = message.visitor_avatar;
                            }
                        } else if (from === 'agent' || from === 'admin') {
                            // 即使无 avatar_url 也展示首字母占位（AI / TG reply / 旧消息历史）
                            if (conf.show_agent_avatar !== false) {
                                showAvatar = true;
                                avatarUrl = message.agent_avatar || null;
                            }
                            if (message.agent_name) {
                                agentName = message.agent_name;
                                displayName = null;
                            }
                        }

                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className={from + ' chat-msg reply-msg' + enterCls}
                                key={messageKey}
                                ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                onTouchStart={(e) => this.handleTouchStart(messageId, message, e)}
                                onTouchEnd={this.handleTouchEnd}
                                onMouseDown={(e) => this.handleMouseDown(messageId, message, e)}
                                onMouseUp={this.handleMouseUp}
                                onContextMenu={(e) => this.handleContextMenu(e, messageId, message)}
                                onTouchCancel={this.handleTouchEnd}
                                onMouseLeave={(e) => { this.handleMouseUp(e); this._setHover(null); }}
                                onMouseEnter={() => this._setHover(messageId)}
                                style={{
                                    position: 'relative',   // hover 图标绝对定位的参照
                                    userSelect: this._isCoarsePointer() ? 'none' : 'text',
                                    WebkitTapHighlightColor: 'transparent',
                                    transform: this.state.pressingId === messageId ? 'scale(0.97)' : 'scale(1)',
                                    transition: 'transform var(--lc-dur-base) var(--lc-ease-spring), filter var(--lc-dur-base) var(--lc-ease-out)',
                                    filter: this.state.pressingId === messageId ? 'brightness(0.95)' : 'none',
                                }}
                            >
                                {this._renderHoverActions(messageId, message, from === 'visitor')}
                                {showAvatar && (
                                    <div className="msg-avatar" style={{
                                        width: '36px',
                                        height: '36px',
                                        marginRight: '10px',
                                        flexShrink: 0,
                                        alignSelf: 'flex-end',
                                        borderRadius: 'var(--lc-radius-full)',
                                        overflow: 'hidden',
                                        background: avatarUrl ? 'transparent' : ((conf && conf.mainColor) || '#2563eb'),
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        boxShadow: 'var(--lc-shadow-1)',
                                    }}>
                                        {avatarUrl ? (
                                            <img src={avatarUrl} alt="avatar" style={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: 'var(--lc-radius-full)',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }} />
                                        ) : (
                                            <span>{(agentName || 'CS').charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                )}
                                <div className="msg-wrapper">
                                    {agentName && (
                                        <div className="agent-name" style={{
                                            fontSize: '13px',
                                            color: 'var(--lc-text-2, #64748b)',
                                            marginBottom: 'var(--lc-space-1)',
                                            fontWeight: '500'
                                        }}>{agentName}</div>
                                    )}
                                    <div className="msg">
                                    {/* 显示被回复的消息 */}
                                    <div className="replied-message"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.scrollToMessage(reply_to.id);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="reply-header">
                                            <svg className="reply-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
                                            <span className="reply-from">{replyFromLabel(reply_to.from_user, conf, t)}</span>
                                        </div>
                                        <div className="reply-content">
                                            {reply_to.content && <p>{reply_to.content}</p>}
                                            {reply_to.attachment && (
                                                <div className="reply-attachment">
                                                    {isVoiceAttachmentUrl(reply_to.attachment) ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--lc-space-1)', fontSize: '12px', color: 'var(--lc-text-2, #666)' }}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/></svg>
                                                            {t('voice.record')}
                                                        </span>
                                                    ) : reply_to.attachment.indexOf('.webm') === -1 ? (
                                                        <img src={reply_to.attachment} style={{ maxWidth: '60px', height: '40px', objectFit: 'cover' }} alt="attachment" />
                                                    ) : (
                                                        <video
                                                            src={reply_to.attachment}
                                                            style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                                                        ></video>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* 回复消息的主体 */}
                                    {this.renderMessageBody(text, displayName, message)}
                                    {attachment && isVoiceAttachmentUrl(attachment) && (
                                        <div className="chat-voice" style={{ marginTop: 'var(--lc-space-1)' }}>
                                            <VoicePlayer src={attachment} durationHint={this._voiceDurationHint(message)} />
                                        </div>
                                    )}
                                    {attachment && !isVoiceAttachmentUrl(attachment) && (
                                        <div className="chat-ani">
                                            {attachment.indexOf('.webm') === -1 ? (
                                                <img
                                                    src={attachment}
                                                    style={{ maxWidth: '100%', height: 'auto', cursor: 'pointer' }}
                                                    className="chat-ani"
                                                    alt="attachment"
                                                    onClick={() => this.openImagePreview(attachment)}
                                                    onLoad={(e) => {
                                                        // 图片加载完成后触发滚动
                                                        if (this.props.onImageLoad) {
                                                            this.props.onImageLoad();
                                                        }
                                                    }}
                                                    onError={(e) => {
                                                        // 加载失败时设置占位图
                                                        e.target.style.minHeight = '100px';
                                                        e.target.style.backgroundColor = 'var(--lc-surface-2, #f0f0f0)';
                                                    }}
                                                />
                                            ) : (
                                                <video
                                                    className="sticker chat-ani"
                                                    src={attachment}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    poster=""
                                                    style={{ maxWidth: '150px', maxHeight: '150px', display: 'block' }}
                                                    onLoadedData={(e) => {
                                                        // 视频元数据加载完成后触发滚动
                                                        if (this.props.onImageLoad) {
                                                            this.props.onImageLoad();
                                                        }
                                                    }}
                                                ></video>
                                            )}
                                        </div>
                                    )}
                                    </div>
                                    {this._renderSendStatus(message)}
                                </div>
                            </li>
                            </Fragment>
                        );
                    }

                    // 其他 system 消息统一用居中提示样式，无时间戳
                    if (from === 'system') {
                        return (
                            <Fragment key={`frag-${messageKey}`}>
                                {TimeLabel}
                            <li className="system chat-msg" key={messageKey}
                                ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                                style={{ listStyle: 'none' }}
                            >
                                <div className="msg" style={{
                                    background: 'var(--lc-surface-2, #f0f0f0)',
                                    color: 'var(--lc-text-2, #666)',
                                    padding: '6px 14px',
                                    borderRadius: 'var(--lc-radius-sm)',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    display: 'inline-block'
                                }}>
                                    <span>{text}</span>
                                </div>
                            </li>
                            </Fragment>
                        );
                    }

                    // 处理普通消息
                    let displayName = name;
                    let showAvatar = false;
                    let avatarUrl = null;
                    let agentName = null;

                    if (from === 'visitor') {
                        displayName = null; // 访客消息不显示前缀
                        // 访客可以显示头像（如果有的话）
                        if (message.visitor_avatar) {
                            showAvatar = true;
                            avatarUrl = message.visitor_avatar;
                        }
                    } else if (from === 'agent' || from === 'admin') {
                        // 客服显示头像和名称（show_agent_avatar 默认 true）
                        // 即使 message.agent_avatar 为空（AI 消息 / TG reply / 旧消息）也显示首字母占位
                        if (conf.show_agent_avatar !== false) {
                            showAvatar = true;
                            avatarUrl = message.agent_avatar || null;
                        }
                        if (message.agent_name) {
                            // The server's fallback sender name; a merchant's title is left as is.
                            agentName = (message.agent_name === 'Customer Service' && conf.service_title_is_default)
                                ? t('brand.customer_service')
                                : message.agent_name;
                            displayName = null; // 客服名称单独显示
                        }
                    }

                    // 连续消息分组（Crisp 结构）：同一发送者连续多条只在第一条挂
                    // 头像和名字，后续消息用等宽占位保持左边距一致；时间分隔线 /
                    // 系统消息 / 客服换人都会重新开组。
                    const prevMsgForGroup = index > 0 ? messages[index - 1] : null;
                    const isGroupStart = shouldShowTime
                        || !prevMsgForGroup
                        || prevMsgForGroup.from !== from
                        || prevMsgForGroup.from === 'system'
                        || prevMsgForGroup.type === 'system'
                        || ((from === 'agent' || from === 'admin')
                            && (prevMsgForGroup.agent_name || null) !== (message.agent_name || null));

                    // 头像挂在组的**最后一条**旁并底对齐（IM 惯例：Intercom/微信/iMessage
                    // 都是"说话的人在下面托着这段话"），名字仍留在组首。isGroupEnd 与
                    // isGroupStart 对称：看**下一条**是否另起一组（换发送方 / 隔了时间线 /
                    // 客服换人 / 到底了）。下一条的时间线判定要和上面 shouldShowTime 同口径。
                    const nextMsgForGroup = index < messages.length - 1 ? messages[index + 1] : null;
                    let nextShowsTime = false;
                    if (conf.displayMessageTime && nextMsgForGroup && nextMsgForGroup.time && time) {
                        const curT = new Date(time);
                        const nxtT = new Date(nextMsgForGroup.time);
                        if (Math.abs(nxtT - curT) > TIME_DIVIDER_GAP_MS) nextShowsTime = true;
                        if (nxtT.getDate() !== curT.getDate()
                            || nxtT.getMonth() !== curT.getMonth()
                            || nxtT.getFullYear() !== curT.getFullYear()) nextShowsTime = true;
                    }
                    const isGroupEnd = !nextMsgForGroup
                        || nextMsgForGroup.from !== from
                        || nextMsgForGroup.from === 'system'
                        || nextMsgForGroup.type === 'system'
                        || nextShowsTime
                        || ((from === 'agent' || from === 'admin')
                            && (nextMsgForGroup.agent_name || null) !== (message.agent_name || null));

                    return (
                        <Fragment key={`frag-${messageKey}`}>
                            {TimeLabel}
                        <li className={from + ' chat-msg' + enterCls}
                            key={messageKey}
                            ref={(el) => { if (el) this.messageRefs[messageId] = el; }}
                            onTouchStart={(e) => this.handleTouchStart(messageId, message, e)}
                            onTouchEnd={this.handleTouchEnd}
                            onMouseDown={(e) => this.handleMouseDown(messageId, message, e)}
                            onMouseUp={this.handleMouseUp}
                            onMouseLeave={(e) => { this.handleMouseUp(e); this._setHover(null); }}
                            onMouseEnter={() => this._setHover(messageId)}
                            onTouchCancel={this.handleTouchEnd}
                            onContextMenu={(e) => this.handleContextMenu(e, messageId, message)}
                            style={{
                                position: 'relative',   // hover 图标绝对定位的参照
                                userSelect: this._isCoarsePointer() ? 'none' : 'text',
                                WebkitTapHighlightColor: 'transparent',
                                transform: this.state.pressingId === messageId ? 'scale(0.97)' : 'scale(1)',
                                transition: 'transform var(--lc-dur-base) var(--lc-ease-spring), filter var(--lc-dur-base) var(--lc-ease-out)',
                                filter: this.state.pressingId === messageId ? 'brightness(0.95)' : 'none',
                            }}
                        >
                            {this._renderHoverActions(messageId, message, from === 'visitor')}
                            {showAvatar && (isGroupEnd ? (
                                <div className="msg-avatar" style={{
                                    width: '36px',
                                    height: '36px',
                                    marginRight: '10px',
                                    flexShrink: 0,
                                    alignSelf: 'flex-end',
                                    borderRadius: 'var(--lc-radius-full)',
                                    overflow: 'hidden',
                                    background: avatarUrl ? 'transparent' : ((conf && conf.mainColor) || '#2563eb'),
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    boxShadow: 'var(--lc-shadow-1)',
                                }}>
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="avatar" style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: 'var(--lc-radius-full)',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }} onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 10-16 0"/></svg>');
                                            e.target.style.background = 'var(--lc-surface-3, #e2e8f0)';
                                            e.target.style.padding = '4px';
                                        }} />
                                    ) : (
                                        <span>{(agentName || 'CS').charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                            ) : (
                                /* 非组尾消息：等宽占位保持气泡左缘对齐（36px 头像 + 10px 间距）；头像只挂组尾 */
                                <div className="msg-avatar-spacer" style={{ width: '46px', flexShrink: 0 }} aria-hidden="true" />
                            ))}
                            <div className="msg-wrapper" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                maxWidth: '80%'
                            }}>
                                {agentName && isGroupStart && (
                                    <div className="agent-name" style={{
                                        fontSize: '13px',
                                        color: 'var(--lc-text-2, #64748b)',
                                        marginBottom: 'var(--lc-space-1)',
                                        fontWeight: '500'
                                    }}>{agentName}</div>
                                )}
                                <div className="msg">
                                    {/* 显示被回复的消息 */}
                                    {reply_to && reply_to.id && (
                                    <div className="replied-message"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.scrollToMessage(reply_to.id);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="reply-header">
                                            <svg className="reply-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
                                            <span className="reply-from">{replyFromLabel(reply_to.from_user, conf, t)}</span>
                                        </div>
                                        <div className="reply-content">
                                            {reply_to.content && <p>{reply_to.content}</p>}
                                            {reply_to.attachment && (
                                                <div className="reply-attachment">
                                                    {isVoiceAttachmentUrl(reply_to.attachment) ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--lc-space-1)', fontSize: '12px', color: 'var(--lc-text-2, #666)' }}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/></svg>
                                                            {t('voice.record')}
                                                        </span>
                                                    ) : reply_to.attachment.indexOf('.webm') === -1 ? (
                                                        <img src={reply_to.attachment} style={{ maxWidth: '60px', height: '40px', objectFit: 'cover' }} alt="attachment" />
                                                    ) : (
                                                        <video
                                                            src={reply_to.attachment}
                                                            style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                                                        ></video>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    {/* 回复消息的主体 */}
                                    {this.renderMessageBody(text, displayName, message)}
                                {attachment && isVoiceAttachmentUrl(attachment) && (
                                    <div className="chat-voice" style={{ marginTop: 'var(--lc-space-1)' }}>
                                        <VoicePlayer src={attachment} durationHint={this._voiceDurationHint(message)} />
                                    </div>
                                )}
                                {attachment && !isVoiceAttachmentUrl(attachment) && (
                                    <div className="chat-ani">
                                        {attachment.indexOf('.webm') === -1 ? (
                                            <img
                                                src={attachment}
                                                style={{ maxWidth: '100%', height: 'auto', cursor: 'pointer' }}
                                                className="chat-ani"
                                                alt="attachment"
                                                onClick={() => this.openImagePreview(attachment)}
                                                onLoad={(e) => {
                                                    // 图片加载完成后触发滚动
                                                    if (this.props.onImageLoad) {
                                                        this.props.onImageLoad();
                                                    }
                                                }}
                                                onError={(e) => {
                                                    // 加载失败时设置占位图
                                                    e.target.style.minHeight = '100px';
                                                    e.target.style.backgroundColor = 'var(--lc-surface-2, #f0f0f0)';
                                                }}
                                            />
                                        ) : (
                                            <video
                                                className="sticker chat-ani"
                                                src={attachment}
                                                autoPlay
                                                loop
                                                muted
                                                poster=""
                                                style={{ maxWidth: '150px', maxHeight: '150px', display: 'block' }}
                                                onLoadedData={(e) => {
                                                    // 视频元数据加载完成后触发滚动
                                                    if (this.props.onImageLoad) {
                                                        this.props.onImageLoad();
                                                    }
                                                }}
                                            ></video>
                                        )}
                                    </div>
                                )}
                                {/* Citation chips for AI messages (spec #17 §8.1) */}
                                {this.renderCitationChips(message)}
                                </div>
                                {/* 气泡下方不再挂时间：时间只由气泡**上方**的居中分隔线
                                    (TimeLabel) 承担。此前两套并存，而连续消息会分组（后续
                                    消息不重复名字），气泡时间就夹在同一发送者的两条消息之
                                    间，读起来像一条无主的分隔线。 */}
                                {this._renderSendStatus(message)}
                            </div>
                        </li>
                        </Fragment>
                    );
                })}
            </ol>

            {/* 图片预览 - 使用Portal渲染到body，z-index大于widget */}
            {previewImageUrl && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        zIndex: 2147483648,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'zoom-out'
                    }}
                    onClick={this.closeImagePreview}
                >
                    <button
                        onClick={this.closeImagePreview}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#fff',
                            fontSize: '30px',
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--lc-radius-full)',
                            cursor: 'pointer',
                            lineHeight: '40px',
                            padding: 0,
                            zIndex: 2147483649
                        }}
                    >
                        ×
                    </button>
                    <img
                        src={previewImageUrl}
                        alt="Preview"
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            objectFit: 'contain',
                            borderRadius: 'var(--lc-radius-xs)',
                            cursor: 'default'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>,
                document.body
            )}
            {this._renderActionMenu()}
            {this._renderCopyToast()}
            </>
        );
    }

}
